
import { Separator } from "@/components/ui/separator";
import { FundHeader } from "@/components/fund/FundHeader";
import { CompanyCard } from "@/components/fund/company-details/CompanyCard";
import { FundInfo } from "@/components/fund/fund-info/FundInfo";
import { FundMetrics } from "@/components/fund/FundMetrics";
import { FundReturns } from "@/components/fund/FundReturns";
import { FundDocuments } from "@/components/fund/FundDocuments";
import CompanyFundsTable from "@/components/fund/company-funds/CompanyFundsTable";
import { FundImageUpload } from "@/components/fund/image-management/FundImageUpload";
import { useUserRole } from "@/hooks/useUserRole";
import type { Fund } from "@/hooks/useFundData";
import { useState, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import type { FundRawRecord } from "@/components/funds-raw/types";
import { useAddToPortfolio } from "@/hooks/portfolio/useAddToPortfolio";


interface FundProfileContentProps {
  fund: Fund;
}

export const FundProfileContent = ({ fund }: FundProfileContentProps) => {
  const { hasRole } = useUserRole();
  const { toast } = useToast();
  const [canManageImages, setCanManageImages] = useState(false);
  
  // Provider-based portfolio dialog
  const { openFundsRaw } = useAddToPortfolio();

  useEffect(() => {
    const checkRole = async () => {
      const hasAccess = await hasRole('sys_admin');
      setCanManageImages(hasAccess);
    };
    checkRole();
  }, [hasRole]);

  const processedReturns = fund?.fund_performance
    ?.filter(p => p.metric_name === 'return')
    .sort((a, b) => {
      const dateComparison = new Date(b.date).getTime() - new Date(a.date).getTime();
      if (dateComparison !== 0) return dateComparison;
      if (a.frequency === 'ytd' && b.frequency !== 'ytd') return -1;
      if (a.frequency !== 'ytd' && b.frequency === 'ytd') return 1;
      return 0;
    })
    .map(p => ({
      date: p.date,
      performance_value: p.performance_value,
      frequency: p.frequency
    })) || [];

  // Action handlers for company funds table
  const handleAddToPortfolio = (fundId: string) => {
    // Pass minimal record; dialog will hydrate details as needed
    const minimal: FundRawRecord = { id: fundId } as FundRawRecord;
    openFundsRaw({ fund: minimal, onSuccess: () => {
      toast({
        title: "Success",
        description: "Fund added to portfolio successfully",
      });
    }});
  };

  const handleFavorite = (fundId: string) => {
    console.log(`Add fund ${fundId} to favorites`);
    toast({
      title: "Feature coming soon",
      description: "Favorites functionality will be available in a future update."
    });
    // TODO: Implement favorite functionality
  };
  
  const handleAddToWatchlist = (fundId: string) => {
    console.log(`Add fund ${fundId} to watchlist`);
    toast({
      title: "Feature coming soon",
      description: "Watchlist functionality will be available in a future update."
    });
    // TODO: Implement watchlist functionality
  };

  return (
    <>
      <FundHeader fundName={fund.fund_name} fundId={fund.id} />

      {fund.company && (
        <CompanyCard
          companyName={fund.company.fund_company_name}
          websiteUrl={fund.company.website_url}
          description={fund.company.description}
          logoUrl={fund.company.logo_url}
          companyId={fund.company.id}
          fundId={fund.id}
        />
      )}

      <Separator />
      <div className="space-y-6">
        <FundInfo
          strategy={fund.investment_strategy}
          framework={fund.fund_framework}
          description={fund.description}
        />
      </div>

      <Separator />
      <FundMetrics
        fundSize={fund.fund_size}
        managementFee={fund.management_fee}
        performanceFee={fund.performance_fee}
        inceptionDate={fund.inception_date}
      />

      {fund.company?.fund_contacts && (fund.company.fund_contacts?.length || 0) > 0 && (
        <>
          <Separator />
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Key Contacts</h3>
            <CompanyCard
              companyName={fund.company.fund_company_name}
              contacts={fund.company.fund_contacts}
              fundId={fund.id}
            />
          </div>
        </>
      )}

      <FundReturns performance={processedReturns} />

      <FundDocuments fundId={fund.id} />
      
      <Separator />
      <CompanyFundsTable 
        fundId={fund.id}
        companyId={fund.company?.id}
        companyName={fund.company?.fund_company_name}
        companyWebsiteUrl={fund.company?.website_url}
        onAddToPortfolio={handleAddToPortfolio}
        onFavorite={handleFavorite}
        onAddToWatchlist={handleAddToWatchlist}
      />

      {canManageImages && (
        <>
          <Separator />
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Fund Brand Image</h3>
            <FundImageUpload
              fundId={fund.id}
              currentImageUrl={fund.fund_image_url}
              onImageUpdate={() => {}}
              displayMode="full"
            />
          </div>
        </>
      )}
      
      {/* Portfolio dialog is provided globally by FundsPortfolioDialogProvider */}
    </>
  );
};
